// src/pages/ProductsPage.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import shopApi, { detectCountry } from '../services/shopApi';
import ProductCard from '../components/ProductCard';
import './ProductsPage.css';

// ── Pagination helper ─────────────────────────────────────────────────────────
// Returns page numbers with ellipsis, e.g. [1, '…', 4, 5, 6, '…', 20]
function buildPageRange(current, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);

  const pages = new Set([1, total, current]);
  for (let d = -2; d <= 2; d++) {
    const p = current + d;
    if (p >= 1 && p <= total) pages.add(p);
  }

  const sorted = [...pages].sort((a, b) => a - b);
  const result = [];

  sorted.forEach((p, i) => {
    if (i > 0 && p - sorted[i - 1] > 1) result.push('…');
    result.push(p);
  });

  return result;
}

// ── Price range slider values ─────────────────────────────────────────────────
const SORT_OPTIONS = [
  { value: 'newest',     label: 'Newest'            },
  { value: 'popular',    label: 'Most Popular'       },
  { value: 'price_low',  label: 'Price: Low → High'  },
  { value: 'price_high', label: 'Price: High → Low'  },
  { value: 'bestselling',label: 'Best Selling'       },
  { value: 'rating',     label: 'Top Rated'          },
];

export default function ProductsPage({ onAdd }) {
  const [searchParams, setSearchParams] = useSearchParams();

  // Derived from URL
  const search     = searchParams.get('search')     || '';
  const category   = searchParams.get('category')   || '';
  const sort       = searchParams.get('sort')       || 'newest';
  const onSale     = searchParams.get('onSale')     || '';
  const inStock    = searchParams.get('inStock')    || '';
  const marketTab  = searchParams.get('market')     || 'all'; // 'all' | 'local' | 'global'
  const page       = Math.max(1, parseInt(searchParams.get('page') || '1'));

  // Local state
  const [products,   setProducts]   = useState([]);
  const [categories, setCategories] = useState([]);
  const [total,      setTotal]      = useState(0);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState(null);
  const [adding,     setAdding]     = useState(null);
  const [retryKey,     setRetryKey]     = useState(0);
  const [country,      setCountry]      = useState(null);
  const [countryReady, setCountryReady] = useState(false);
  const [countryName,  setCountryName]  = useState('');
  const [sidebarOpen,  setSidebarOpen]  = useState(false);

  const mainRef = useRef(null);

  // ── Detect country once ───────────────────────────────────────────────────────
  useEffect(() => {
    const timeout = new Promise(resolve => setTimeout(resolve, 3000));
    Promise.race([
      detectCountry().then(geo => geo || {}),
      timeout.then(() => ({})),
    ])
      .then(geo => {
        setCountry(geo.country || '');
        setCountryName(geo.countryName || geo.country || '');
      })
      .catch(() => { setCountry(''); setCountryName(''); })
      .finally(() => setCountryReady(true));
  }, []);

  // ── Load categories once ──────────────────────────────────────────────────────
  useEffect(() => {
    shopApi.getCategories()
      .then(r => { if (r.success) setCategories(r.data.categories || []); })
      .catch(() => setCategories([]));
  }, []);

  // ── Load products when filters change ────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;

    if (!countryReady) return;

    setLoading(true);
    setError(null);

    const params = { search, category, sort, page, limit: 24 };
    if (country)                params.country    = country;
    if (onSale)                 params.onSale     = 'true';
    if (inStock)                params.inStock    = 'true';
    if (marketTab === 'local')  params.localOnly  = 'true';
    if (marketTab === 'global') params.globalOnly = 'true';

    shopApi.getProducts(params)
      .then(r => {
        if (cancelled) return;
        if (r.success) {
          setProducts(r.data.products || []);
          setTotal(r.data.pagination?.total || 0);
        } else {
          setProducts([]);
          setTotal(0);
          setError('Could not load products. Please try again.');
        }
      })
      .catch(() => {
        if (!cancelled) {
          setProducts([]);
          setTotal(0);
          setError('Network error. Please check your connection.');
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [search, category, sort, page, onSale, inStock, country, countryReady, marketTab]);

  // ── Scroll to top of product list on page change ──────────────────────────────
  useEffect(() => {
    mainRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [page]);

  // ── URL param helpers ─────────────────────────────────────────────────────────
  const setParam = (key, val) => {
    const next = new URLSearchParams(searchParams);
    if (val) next.set(key, val); else next.delete(key);
    if (key !== 'page') next.delete('page'); // reset page on filter change
    setSearchParams(next);
  };

  const toggleParam = (key) => {
    setParam(key, searchParams.get(key) ? '' : 'true');
  };

  const clearAllFilters = () => {
    setSearchParams({});
  };

  const hasActiveFilters = search || category || onSale || inStock || sort !== 'newest';

  // ── Add to cart ───────────────────────────────────────────────────────────────
  const handleAdd = async (product) => {
    const key = product.listingId || product._id || product.productId;
    setAdding(key);
    try {
      await onAdd(product); // pass full product so App.jsx uses listingId
    } finally {
      setAdding(null);
    }
  };

  const totalPages = Math.ceil(total / 24);
  const pageRange  = buildPageRange(page, totalPages);

  return (
    <div className="products-page page-wrap">

      {/* Mobile filter toggle */}
      <button
        className="sidebar-toggle-btn"
        onClick={() => setSidebarOpen(o => !o)}
        aria-expanded={sidebarOpen}
        aria-controls="products-sidebar"
      >
        {sidebarOpen ? '✕ Close Filters' : '⚙ Filters'}
        {hasActiveFilters && <span className="filter-badge" />}
      </button>

      <div className="products-layout">

        {/* ── Sidebar ─────────────────────────────────────────────────────── */}
        <aside
          id="products-sidebar"
          className={`sidebar ${sidebarOpen ? 'open' : ''}`}
          aria-label="Product filters"
        >
          {/* Clear filters */}
          {hasActiveFilters && (
            <button className="clear-filters-btn" onClick={clearAllFilters}>
              ✕ Clear all filters
            </button>
          )}

          {/* Categories */}
          <div className="sidebar-section">
            <div className="sidebar-title">Categories</div>
            <button
              className={`sidebar-item ${!category ? 'active' : ''}`}
              onClick={() => setParam('category', '')}
            >
              All Products
              <span className="sidebar-count">{total}</span>
            </button>
            {categories.map(c => (
              <button
                key={c.category}
                className={`sidebar-item ${category === c.category ? 'active' : ''}`}
                onClick={() => { setParam('category', c.category); setSidebarOpen(false); }}
              >
                {c.category}
                <span className="sidebar-count">{c.count}</span>
              </button>
            ))}
          </div>

          {/* Quick filters */}
          <div className="sidebar-section">
            <div className="sidebar-title">Filter By</div>
            <label className="sidebar-check">
              <input
                type="checkbox"
                checked={!!onSale}
                onChange={() => toggleParam('onSale')}
              />
              On Sale
            </label>
            <label className="sidebar-check">
              <input
                type="checkbox"
                checked={!!inStock}
                onChange={() => toggleParam('inStock')}
              />
              In Stock Only
            </label>
          </div>
        </aside>

        {/* ── Main content ─────────────────────────────────────────────────── */}
        <main className="products-main" ref={mainRef}>

          {/* Local / All tabs */}
          <div className="market-tabs">
            {[
              ['all',   '🛍️ All Products'],
              ['local', `📍 Local${countryName ? ' · ' + countryName : ''}`],
              ['global', '🌐 Global'],
            ].map(([v, l]) => (
              <button
                key={v}
                className={`market-tab ${marketTab === v ? 'active' : ''}`}
                onClick={() => setParam('market', v === 'all' ? '' : v)}
              >{l}</button>
            ))}
          </div>

          {/* Top bar */}
          <div className="products-topbar">
            <div className="products-count">
              {loading ? (
                <span>Loading…</span>
              ) : search ? (
                <span>Results for <strong>"{search}"</strong> — {total} products</span>
              ) : category ? (
                <span><strong>{category}</strong> — {total} products</span>
              ) : (
                <span><strong>{total}</strong> products</span>
              )}
            </div>

            <div className="topbar-right">
              {search && (
                <button className="clear-search" onClick={() => setParam('search', '')}>
                  ✕ "{search}"
                </button>
              )}
              {category && (
                <button className="clear-search" onClick={() => setParam('category', '')}>
                  ✕ {category}
                </button>
              )}
              <select
                className="sort-select"
                value={sort}
                onChange={e => setParam('sort', e.target.value)}
                aria-label="Sort products"
              >
                {SORT_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Error */}
          {error && !loading && (
            <div className="products-error" role="alert">
              ⚠️ {error}
              <button onClick={() => setParam('page', String(page))}>Retry</button>
            </div>
          )}

          {/* Grid / empty / loading */}
          {loading ? (
            <div className="products-loading">
              <div className="spinner" />
              <p>Loading products…</p>
            </div>
          ) : products.length > 0 ? (
            <>
              <div className="products-grid">
                {products.map(p => (
                  <ProductCard
                    key={p.listingId || p._id || p.productId}
                    product={p}
                    onAdd={handleAdd}
                    adding={adding === (p.listingId || p._id || p.productId)}
                  />
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <nav className="pagination" aria-label="Page navigation">
                  {/* Prev */}
                  <button
                    className="page-btn page-arrow"
                    disabled={page <= 1}
                    onClick={() => setParam('page', String(page - 1))}
                    aria-label="Previous page"
                  >←</button>

                  {/* Page numbers with ellipsis */}
                  {pageRange.map((p, i) =>
                    p === '…' ? (
                      <span key={`ellipsis-${i}`} className="page-ellipsis">…</span>
                    ) : (
                      <button
                        key={p}
                        className={`page-btn ${page === p ? 'active' : ''}`}
                        onClick={() => setParam('page', String(p))}
                        aria-label={`Page ${p}`}
                        aria-current={page === p ? 'page' : undefined}
                      >{p}</button>
                    )
                  )}

                  {/* Next */}
                  <button
                    className="page-btn page-arrow"
                    disabled={page >= totalPages}
                    onClick={() => setParam('page', String(page + 1))}
                    aria-label="Next page"
                  >→</button>
                </nav>
              )}

              {/* Page info */}
              {totalPages > 1 && (
                <div className="pagination-info">
                  Page {page} of {totalPages} · {total} products
                </div>
              )}
            </>
          ) : (
            <div className="empty-state">
              <div className="icon">🔍</div>
              <h3>No products found</h3>
              <p>
                {search
                  ? `No results for "${search}". Try a different search term.`
                  : 'Try a different category or remove some filters.'}
              </p>
              {hasActiveFilters && (
                <button className="btn-primary" style={{ marginTop: 16 }} onClick={clearAllFilters}>
                  Clear all filters
                </button>
              )}
            </div>
          )}
        </main>
      </div>
    </div>
  );
}